/**
 * useCalendarDrag — encapsulates calendar drag, drop, and undo logic.
 *
 * Before this hook, Dashboard owned ~140 lines of inline handler
 * definitions (`handleDragStart`, `handleDrop`, `addToUndoStack`,
 * `handleUndo`, etc.) plus the `dragOverSlot` state.  Stage 4 of the
 * Calendar refactor moves them here so:
 *
 *   1. Dashboard goes from 8 closures + 1 `useState` to a single hook
 *      call (`useCalendarDrag({ refreshAppointments })`).
 *   2. V2 CalendarRoute, once it mounts `<AppointmentsTab />`
 *      natively, calls the same hook and gets identical behaviour
 *      with zero Dashboard coupling.
 *
 * Why we still take `refreshAppointments` as a param rather than
 * pulling it from a context: it is a Dashboard-owned aggregator that
 * synthesises firm events alongside real appointments. Until that
 * function moves into a context of its own, the hook receives it as
 * an injection so the hook itself stays UI-only and testable.
 *
 * IMPORTANT: there is exactly ONE instance of this hook per logical
 * calendar surface.  Dashboard renders both the inline Calendar (via
 * `<AppointmentsTab />` inside the bridge) and a Fullscreen Calendar
 * Dialog; both currently share Dashboard's single hook instance via
 * the bridge so a drag started in one place finishes correctly even
 * if the user toggles fullscreen mid-drag.
 */

import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAppointments } from '../context/AppointmentsContext';
import { useClients } from '../context/ClientsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_UNDO_STACK = 20;

const noop = () => {};

export const useCalendarDrag = ({ refreshAppointments = noop, cptCodes = [] } = {}) => {
  const {
    appointments, setAppointments,
    draggedAppointment, setDraggedAppointment,
    undoStack, setUndoStack,
  } = useAppointments();

  const { allClientsForLookup } = useClients();

  // dragOverSlot is local to this hook — no other component needs to
  // mutate it directly, so it doesn't belong in AppointmentsContext.
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const undoInFlightRef = useRef(false);
  const [isUndoing, setIsUndoing] = useState(false);

  // ── Undo stack ──────────────────────────────────────────────────
  const addToUndoStack = useCallback(
    (action, appointmentData) => {
      setUndoStack((prev) => {
        const next = [...prev, { action, data: appointmentData, timestamp: Date.now() }];
        return next.slice(-MAX_UNDO_STACK);
      });
    },
    [setUndoStack]
  );

  const handleUndo = useCallback(async () => {
    // Jun 2026 — re-entrancy guard: rapid/held Ctrl+Z (key auto-repeat) or a
    // double-click on the Undo button fired handleUndo multiple times against
    // the same stack top, duplicating restored appointments.
    if (undoInFlightRef.current) return;
    if (undoStack.length === 0) {
      toast.info('Nothing to undo');
      return;
    }
    const lastAction = undoStack[undoStack.length - 1];
    undoInFlightRef.current = true;
    setIsUndoing(true);
    // Pop BEFORE awaiting so concurrent triggers can't re-read the same entry.
    setUndoStack((prev) => prev.slice(0, -1));
    try {
      if (lastAction.action === 'delete') {
        await axios.post(`${API}/appointments`, lastAction.data);
        toast.success('Appointment restored');
      } else if (lastAction.action === 'delete_with_note') {
        // Jun 2026 — undo of "Delete Both": restores the appointment with
        // its original id AND the linked note from deleted_notes.
        const { data } = await axios.post(`${API}/appointments/undo-linked-delete`, lastAction.data);
        toast.success(data?.message || 'Appointment and note restored');
      } else if (lastAction.action === 'update') {
        await axios.patch(`${API}/appointments/${lastAction.data.id}`, {
          start_time: lastAction.data.start_time,
          end_time: lastAction.data.end_time,
          client_id: lastAction.data.client_id,
          provider_id: lastAction.data.provider_id,
          service_type: lastAction.data.service_type,
          units: lastAction.data.units,
        });
        toast.success('Appointment change undone');
      }
      await refreshAppointments(true);
    } catch (error) {
      console.error('Undo failed:', error);
      toast.error('Failed to undo action');
      // Put the entry back so the user can retry.
      setUndoStack((prev) => [...prev, lastAction]);
    } finally {
      undoInFlightRef.current = false;
      setIsUndoing(false);
    }
  }, [undoStack, setUndoStack, refreshAppointments]);

  // ── Drag handlers ───────────────────────────────────────────────
  const handleDragStart = useCallback(
    (e, appointment) => {
      setDraggedAppointment(appointment);
      e.dataTransfer.effectAllowed = 'move';
      if (e.target) {
        e.target.style.opacity = '0.6';
        e.target.style.transform = 'scale(1.05)';
      }
      const clientName =
        allClientsForLookup.find((c) => c.id === appointment.client_id)?.full_name ||
        'Appointment';
      toast.info(`✋ Dragging: ${clientName}`, {
        duration: 2000,
        icon: '📅',
        style: { background: '#3B82F6', color: 'white' },
      });
    },
    [setDraggedAppointment, allClientsForLookup]
  );

  const handleAppointmentDragEnd = useCallback(
    (e) => {
      if (e.target) {
        e.target.style.opacity = '1';
        e.target.style.transform = 'scale(1)';
      }
      setDragOverSlot(null);
      setTimeout(() => {
        if (draggedAppointment) setDraggedAppointment(null);
      }, 100);
    },
    [draggedAppointment, setDraggedAppointment]
  );

  const handleDragOver = useCallback((e, slotInfo) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (slotInfo) setDragOverSlot(slotInfo);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverSlot(null);
  }, []);

  // ── View-agnostic drop core (Jun 2026) ───────────────────────────
  // Normalized params so month / week / day / dnd-kit touch paths all
  // share identical behaviour: { targetDate, appointmentId,
  // targetProviderId? }.  Does not implicitly depend on view state.
  const performDrop = useCallback(
    async ({ targetDate, appointmentId, targetProviderId }) => {
      const sourceApt =
        appointments.find((a) => a.id === appointmentId) ||
        (draggedAppointment?.id === appointmentId ? draggedAppointment : null);
      if (!sourceApt || !targetDate) return;

      try {
        const originalStart = new Date(sourceApt.start_time);
        const originalEnd = new Date(sourceApt.end_time);
        const duration = originalEnd - originalStart;
        const newDate = new Date(targetDate);
        const newEndDate = new Date(newDate.getTime() + duration);
        const providerId = targetProviderId || sourceApt.provider_id;

        // Conflict pre-checks are best-effort UI validation — the
        // backend 409 is ground truth and the catch below rolls back.
        // Per-CPT overlap allowance: skipped ONLY when BOTH the dragged
        // appointment's CPT and the existing one's CPT allow overlapping.
        const _cptAllowsOverlap = (apt) => {
          const list = cptCodes || [];
          let c = apt.cpt_code_id ? list.find((x) => x.id === apt.cpt_code_id) : null;
          if (!c && apt.service_type) {
            const prefix = String(apt.service_type).split(' - ')[0].trim();
            c = list.find((x) => x.code === prefix || x.display_name === prefix);
          }
          return !!(c && c.allows_overlap);
        };
        const draggedAllowsOverlap = _cptAllowsOverlap(sourceApt);
        const _overlaps = (apt) => {
          const aptStart = new Date(apt.start_time);
          const aptEnd = new Date(apt.end_time);
          return newDate < aptEnd && newEndDate > aptStart;
        };

        const conflictingApt = appointments.find((apt) => {
          if (apt.id === sourceApt.id) return false;
          if (apt.provider_id !== providerId) return false;
          if (draggedAllowsOverlap && _cptAllowsOverlap(apt)) return false;
          return _overlaps(apt);
        });

        if (conflictingApt) {
          const conflictClient = allClientsForLookup.find(
            (c) => c.id === conflictingApt.client_id
          );
          toast.error(
            `Conflict: Provider already has appointment with ${conflictClient?.full_name || 'another client'} at this time. ` +
              `Please schedule or reschedule appointment for a different time.`
          );
          setDraggedAppointment(null);
          return;
        }

        // Client-overlap pre-check (Jun 2026) — mirrors the provider
        // check.  Cancelled / no-show appointments are ignored so a
        // client CAN be rebooked over their own cancelled slot.
        const _isInactive = (a) => {
          const s = (a?.status || '').toLowerCase();
          return s === 'cancelled' || s === 'canceled' || s === 'client_cancelled' || s === 'no_show';
        };
        const clientConflict = sourceApt.client_id
          ? appointments.find((apt) => {
              if (apt.id === sourceApt.id) return false;
              if (!apt.client_id || apt.client_id !== sourceApt.client_id) return false;
              if (_isInactive(apt)) return false;
              if (draggedAllowsOverlap && _cptAllowsOverlap(apt)) return false;
              return _overlaps(apt);
            })
          : null;

        if (clientConflict) {
          const conflictClient = allClientsForLookup.find(
            (c) => c.id === clientConflict.client_id
          );
          toast.error(
            `Conflict: ${conflictClient?.full_name || 'This client'} already has an appointment at this time. ` +
              `Please choose a different time.`
          );
          setDraggedAppointment(null);
          return;
        }

        // Save previous state for undo BEFORE the optimistic update.
        addToUndoStack('update', {
          id: sourceApt.id,
          start_time: sourceApt.start_time,
          end_time: sourceApt.end_time,
          client_id: sourceApt.client_id,
          provider_id: sourceApt.provider_id,
          service_type: sourceApt.service_type,
          units: sourceApt.units,
        });

        // Optimistic UI update.
        const newStartIso = newDate.toISOString();
        const newEndIso = newEndDate.toISOString();
        const providerChanged = !!targetProviderId && targetProviderId !== sourceApt.provider_id;
        setAppointments((prev) =>
          prev.map((apt) =>
            apt.id === sourceApt.id
              ? {
                  ...apt,
                  start_time: newStartIso,
                  end_time: newEndIso,
                  ...(providerChanged ? { provider_id: targetProviderId } : {}),
                }
              : apt
          )
        );

        await axios.patch(`${API}/appointments/${sourceApt.id}`, {
          start_time: newStartIso,
          end_time: newEndIso,
          ...(providerChanged ? { provider_id: targetProviderId } : {}),
        });

        toast.success('Appointment rescheduled (Ctrl+Z to undo)');
        setDraggedAppointment(null);
      } catch (error) {
        console.error('Error rescheduling appointment:', error);
        // Backend is ground truth (e.g. 409 conflict) — surface its
        // specific message and roll back the optimistic update by
        // refetching server state.
        const detail = error?.response?.data?.detail;
        toast.error(
          typeof detail === 'string' && detail ? detail : 'Failed to reschedule appointment'
        );
        refreshAppointments(true);
        setDraggedAppointment(null);
      }
    },
    [
      draggedAppointment,
      appointments,
      allClientsForLookup,
      addToUndoStack,
      setAppointments,
      setDraggedAppointment,
      refreshAppointments,
      cptCodes,
    ]
  );

  // Thin event-facing wrapper kept for the native HTML5 drop paths.
  // `e` may be null (dnd-kit touch path).  opts.appointmentId /
  // opts.targetProviderId override the implicit dragged state.
  const handleDrop = useCallback(
    (e, newDate, opts = {}) => {
      e?.preventDefault?.();
      setDragOverSlot(null);
      const appointmentId = opts.appointmentId || draggedAppointment?.id;
      if (!appointmentId) return Promise.resolve();
      return performDrop({
        targetDate: newDate,
        appointmentId,
        targetProviderId: opts.targetProviderId,
      });
    },
    [draggedAppointment, performDrop]
  );

  return {
    dragOverSlot,
    setDragOverSlot,
    addToUndoStack,
    handleUndo,
    isUndoing,
    handleDragStart,
    handleDragOver,
    handleDrop,
    performDrop,
    handleDragLeave,
    handleAppointmentDragEnd,
  };
};

export default useCalendarDrag;
